import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { LogIn, Building2 } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

/**
 * Hidden sign-on page reserved for hosts. Admins share this link with
 * approved hosts so they can access their portal without navigating the
 * guest-facing site. Unlinked from the main nav on purpose.
 */
export default function HostSignInPage() {
  const { login, refresh } = useAuth();
  const navigate = useNavigate();

  const [signIn, setSignIn] = useState({ email: "", password: "" });
  const [register, setRegister] = useState({ name: "", email: "", password: "" });
  const [busyIn, setBusyIn] = useState(false);
  const [busyReg, setBusyReg] = useState(false);

  const submitSignIn = async (e) => {
    e.preventDefault();
    setBusyIn(true);
    try {
      await login(signIn.email, signIn.password);
      toast.success("Welcome, host.");
      navigate("/host");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sign in failed.");
    } finally {
      setBusyIn(false);
    }
  };

  const submitRegister = async (e) => {
    e.preventDefault();
    setBusyReg(true);
    try {
      const r = await api.post("/auth/register", { ...register, role: "host" });
      try {
        window.localStorage.setItem("sasha_token", r.data.token);
      } catch (_) {
        /* ignore */
      }
      toast.success("Account created — please submit your host application next.");
      await refresh();
      navigate("/hosts/tiers");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not create account.");
    } finally {
      setBusyReg(false);
    }
  };

  return (
    <div
      className="max-w-5xl mx-auto px-6 lg:px-12 py-16"
      data-testid="host-signin-page"
    >
      <div className="text-center max-w-2xl mx-auto">
        <Building2 size={28} strokeWidth={1.25} className="text-[var(--gold)] mx-auto" />
        <div className="overline text-[var(--brand-red)] mt-4">Hosts only</div>
        <h1 className="font-display text-5xl text-[var(--emerald)] mt-3">
          Host sign-in.
        </h1>
        <p
          className="text-[var(--ink-2)] mt-4"
          style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.25rem" }}
        >
          A private entrance for Janté hosts. After application review and intake, your
          portal unlocks here.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-6 mt-12">
        {/* Sign in */}
        <form onSubmit={submitSignIn} className="lux-card" data-testid="host-signin-form">
          <div className="flex items-center gap-2">
            <LogIn size={16} strokeWidth={1.25} className="text-[var(--gold)]" />
            <div className="overline">Sign in</div>
          </div>
          <h2 className="font-display text-2xl text-[var(--emerald)] mt-3">Welcome back</h2>
          <div className="mt-5 space-y-4">
            <div>
              <div className="overline mb-1">Email</div>
              <input
                className="lux-input"
                type="email"
                required
                value={signIn.email}
                onChange={(e) => setSignIn({ ...signIn, email: e.target.value })}
                data-testid="host-signin-email"
              />
            </div>
            <div>
              <div className="overline mb-1">Password</div>
              <input
                className="lux-input"
                type="password"
                required
                value={signIn.password}
                onChange={(e) => setSignIn({ ...signIn, password: e.target.value })}
                data-testid="host-signin-password"
              />
            </div>
            <button
              type="submit"
              disabled={busyIn}
              className="btn-primary w-full"
              data-testid="host-signin-submit"
            >
              {busyIn ? "Signing in…" : "Enter host portal"}
            </button>
          </div>
        </form>

        {/* Register */}
        <form onSubmit={submitRegister} className="lux-card" data-testid="host-register-form">
          <div className="flex items-center gap-2">
            <Building2 size={16} strokeWidth={1.25} className="text-[var(--gold)]" />
            <div className="overline">Create host account</div>
          </div>
          <h2 className="font-display text-2xl text-[var(--emerald)] mt-3">Apply to host</h2>
          <p className="text-[11px] text-[var(--muted)] mt-2">
            Account first — then your application goes to the host partnerships team.
          </p>
          <div className="mt-5 space-y-4">
            <div>
              <div className="overline mb-1">Name</div>
              <input
                className="lux-input"
                required
                value={register.name}
                onChange={(e) => setRegister({ ...register, name: e.target.value })}
                data-testid="host-register-name"
              />
            </div>
            <div>
              <div className="overline mb-1">Email</div>
              <input
                className="lux-input"
                type="email"
                required
                value={register.email}
                onChange={(e) => setRegister({ ...register, email: e.target.value })}
                data-testid="host-register-email"
              />
            </div>
            <div>
              <div className="overline mb-1">Password</div>
              <input
                className="lux-input"
                type="password"
                required
                minLength={6}
                value={register.password}
                onChange={(e) => setRegister({ ...register, password: e.target.value })}
                data-testid="host-register-password"
              />
            </div>
            <button
              type="submit"
              disabled={busyReg}
              className="btn-gold w-full"
              data-testid="host-register-submit"
            >
              {busyReg ? "Creating…" : "Create host account"}
            </button>
          </div>
        </form>
      </div>

      <p className="text-xs text-[var(--muted)] mt-10 text-center">
        Guests, your entrance is{" "}
        <Link to="/guest-signin" className="text-[var(--gold)] hover:underline">
          here
        </Link>
        .
      </p>
    </div>
  );
}
